import { ChangeDetectionStrategy, Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { InputNumberModule } from 'primeng/inputnumber';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { CheckboxModule } from 'primeng/checkbox';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { LambdaService } from 'src/app/services/lambda.service';
import { SpinnerService } from 'src/app/services/spinner.service';

interface Shop {
  id: number;
  name: string;
  infobip_api_key?: string;
  infobip_api_host_uri?: string;
  shopGuid?: string;
}

interface ShopPoint {
  id: number;
  name: string;
}

interface SocialChannel {
  id: number;
  shopId: number;
  shopGuid: string;
  channelType: string;
  channelId: string;
  sender: string;
  apiKey: string;
  apiHostUri: string;
  isActive: boolean;
  shopAgentId: string;
  shopPointId: string;
}

@Component({
  selector: 'app-social-channels',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ToastModule,
    DialogModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    InputNumberModule,
    ConfirmDialogModule,
    CheckboxModule,
    CardModule,
    TagModule,
    TooltipModule,
    ProgressSpinnerModule
  ],
  templateUrl: './social-channels.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MessageService, ConfirmationService]
})
export class SocialChannels implements OnInit {
  shops: Shop[] = [];
  shopPoints: ShopPoint[] = [];
  socialChannels: SocialChannel[] = [];

  selectedShop: Shop | null = null;
  showDialog = false;
  isEditMode = false;
  loading = false;
  isSaving = false;
  searchTerm: string = '';

  form!: FormGroup;

  channelTypes = [
    { label: 'WhatsApp', value: 'whatsapp' },
    { label: 'Live Chat', value: 'live_chat' },
    { label: 'Telegram', value: 'telegram' },
    { label: 'SMS', value: 'sms' },
    { label: 'Email', value: 'email' },
    { label: 'Facebook', value: 'facebook' },
    { label: 'Instagram', value: 'instagram' },
  ];

  constructor(
    private lambdaService: LambdaService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private spinnerService: SpinnerService
  ) {
    this.initForm();
  }

  ngOnInit() {
    this.loadShops();
    this.loadAllSocialChannels();
  } initForm() {
    this.form = this.fb.group({
      id: [null],
      shopId: ['', Validators.required],
      channelType: ['', Validators.required],
      channelId: ['', Validators.required],
      sender: ['', Validators.required],
      apiKey: ['', Validators.required],
      apiHostUri: ['', Validators.required],
      isActive: [true],
      shopPointId: ['', Validators.required]
    });
  }

  loadShops() {
    this.spinnerService.showSpinner();
    this.lambdaService.getShops().then(
      (data: any) => {
        const shopsArray = Array.isArray(data) ? data : (data?.data || data?.shops || []);
        this.shops = shopsArray.map((shop: any) => ({
          id: shop.shopId,
          name: shop.shopName,
          infobip_api_key: shop.infobip_api_key,
          infobip_api_host_uri: shop.infobip_api_host_uri,
          shopGuid: shop.shopGuid
        }));
        this.spinnerService.hideSpinner();
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudieron cargar las tiendas'
        });
        this.spinnerService.hideSpinner();
      }
    );
  }

  loadAllSocialChannels() {
    this.spinnerService.showSpinner();
    this.lambdaService.getShopSocialChannels().then(
      (data: any) => {
        const channelsArray = Array.isArray(data) ? data : (data?.data || data?.channels || []);
        this.socialChannels = channelsArray.map((channel: any) => ({
          ...channel,
          channelType: channel.channelType?.toLowerCase() || channel.channelType
        }));
        this.spinnerService.hideSpinner();
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudieron cargar las redes sociales'
        });
        this.spinnerService.hideSpinner();
      }
    );
  }

  onShopChange(shop: Shop | null) {
    if (shop) {
      this.selectedShop = shop;
      this.loadShopPoints(shop.id);
      this.cdr.markForCheck();
    }
  }

  onShopDropdownChange(event: any) {
    const shopId = event.value;
    const shop = this.shops.find(s => s.id === shopId);
    if (shop) {
      this.onShopChange(shop);
      this.form.patchValue({
        apiKey: shop.infobip_api_key || '',
        apiHostUri: shop.infobip_api_host_uri || ''
      });
    }
  }

  loadShopPoints(shopId: number) {
    this.spinnerService.showSpinner();
    this.lambdaService.getShopPoints(shopId).then(
      (data: any) => {
        const pointsArray = Array.isArray(data) ? data : (data?.data || data?.shopPoints || []);
        this.shopPoints = pointsArray.map((point: any) => ({
          id: point.shopPointId || point.id,
          name: point.shopPointName || point.name
        }));
        this.spinnerService.hideSpinner();
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudieron cargar los puntos de venta'
        });
        this.spinnerService.hideSpinner();
      }
    );
  }

  loadSocialChannels(shopId: number) {
    this.spinnerService.showSpinner();
    this.lambdaService.getShopSocialChannelsByShopId(shopId).then(
      (data: any) => {
        const channelsArray = Array.isArray(data) ? data : [];
        this.socialChannels = channelsArray.map((channel: any) => ({
          ...channel,
          channelType: channel.channelType?.toLowerCase() || channel.channelType
        }));
        this.spinnerService.hideSpinner();
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudieron cargar las redes sociales'
        });
        this.spinnerService.hideSpinner();
      }
    );
  }

  openDialog(channel?: SocialChannel) {
    this.isEditMode = !!channel;
    if (channel) {
      this.form.patchValue(channel);
      this.loadShopPoints(channel.shopId);
    } else {
      this.form.reset({ isActive: true });
    }
    this.showDialog = true;
    this.cdr.markForCheck();
  }

  closeDialog() {
    this.showDialog = false;
    this.form.reset({ isActive: true });
    this.cdr.markForCheck();
  }

  saveChannel() {
    if (!this.form.valid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'Por favor complete todos los campos requeridos'
      });
      return;
    }

    this.isSaving = true;
    this.cdr.markForCheck();
    const formValue = this.form.value;
    const shop = this.shops.find(s => s.id === formValue.shopId);
    const shopGuid = shop?.shopGuid || formValue.shopId.toString();

    if (this.isEditMode) {
      this.lambdaService.updateShopSocialChannel(
        formValue.id,
        formValue.shopId,
        formValue.channelType,
        formValue.channelId,
        formValue.sender,
        formValue.apiKey,
        formValue.apiHostUri,
        formValue.isActive,
        formValue.shopPointId
      ).then(
        (response: any) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: 'Red social actualizada correctamente'
          });
          this.closeDialog();
          this.loadAllSocialChannels();
          this.isSaving = false;
          this.cdr.markForCheck();
        },
        (error: any) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo actualizar la red social'
          });
          this.isSaving = false;
          this.cdr.markForCheck();
        }
      );
    } else {
      this.lambdaService.createShopSocialChannel(
        formValue.shopId,
        shopGuid,
        formValue.channelType,
        formValue.channelId,
        formValue.sender,
        formValue.apiKey,
        formValue.apiHostUri,
        formValue.isActive,
        formValue.shopPointId
      ).then(
        (response: any) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: 'Red social creada correctamente'
          });
          this.closeDialog();
          this.loadAllSocialChannels();
          this.isSaving = false;
          this.cdr.markForCheck();
        },
        (error: any) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo crear la red social'
          });
          this.isSaving = false;
          this.cdr.markForCheck();
        }
      );
    }
  }

  deleteChannel(channel: SocialChannel) {
    this.confirmationService.confirm({
      message: `¿Está seguro de que desea eliminar la red social ${channel.channelType}?`,
      header: 'Confirmar eliminación',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.isSaving = true;
        this.cdr.markForCheck();
        this.lambdaService.deleteShopSocialChannel(channel.id, channel.shopId).then(
          (response: any) => {
            this.messageService.add({
              severity: 'success',
              summary: 'Éxito',
              detail: 'Red social eliminada correctamente'
            });
            this.loadAllSocialChannels();
            this.isSaving = false;
            this.cdr.markForCheck();
          },
          (error: any) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'No se pudo eliminar la red social'
            });
            this.isSaving = false;
            this.cdr.markForCheck();
          }
        );
      }
    });
  }

  getChannelTypeName(value: string): string {
    return this.channelTypes.find(ct => ct.value === value)?.label || value;
  }

  getShopName(shopId: number): string {
    return this.shops.find(s => s.id === shopId)?.name || `${shopId}`;
  }

  getFilteredChannels(): SocialChannel[] {
    if (!this.searchTerm.trim()) {
      return this.socialChannels;
    }

    const term = this.searchTerm.toLowerCase();
    return this.socialChannels.filter(channel => {
      const shopName = this.getShopName(channel.shopId).toLowerCase();
      const channelType = this.getChannelTypeName(channel.channelType).toLowerCase();
      const sender = (channel.sender || '').toLowerCase();
      const channelId = (channel.channelId || '').toLowerCase();

      return (
        shopName.includes(term) ||
        channelType.includes(term) ||
        sender.includes(term) ||
        channelId.includes(term) ||
        channel.shopId.toString().includes(term)
      );
    });
  }

  onSearchChange(): void {
    this.cdr.markForCheck();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.cdr.markForCheck();
  }
}
